import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.jpg';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Forgot password form
  const [email, setEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Reset password form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordRequirements = [
    { label: 'Mínimo 8 caracteres', valid: newPassword.length >= 8 },
    { label: 'Al menos una mayúscula', valid: /[A-Z]/.test(newPassword) },
    { label: 'Al menos una minúscula', valid: /[a-z]/.test(newPassword) },
    { label: 'Al menos un número', valid: /\d/.test(newPassword) },
    { label: 'Al menos un carácter especial', valid: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword) },
  ];

  const allRequirementsMet = passwordRequirements.every(r => r.valid);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.requestPasswordReset(email);
      setForgotSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al enviar el enlace');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (!allRequirementsMet) {
      setError('La contraseña no cumple todos los requisitos');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(token!, newPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const BrandPanel = () => (
    <div className="reset-brand-panel">
      <img src={logo} alt="Club Participa" className="reset-brand-logo" />
      <div className="reset-brand-tagline">
        <h1>Gestión de inversiones</h1>
        <p>Plataforma integral para el seguimiento de tus productos de inversión</p>
      </div>
    </div>
  );

  // Reset password success
  if (token && resetSuccess) {
    return (
      <div className="reset-page">
        <div className="reset-wrapper">
          <BrandPanel />
          <div className="reset-form-panel">
            <div className="reset-form-inner">
              <div className="success-message">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h2>Contraseña actualizada</h2>
                <p>Tu contraseña se ha restablecido correctamente.</p>
                <Link to="/login" className="reset-button-link">Ir a iniciar sesión</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form (with token)
  if (token) {
    return (
      <div className="reset-page">
        <div className="reset-wrapper">
          <BrandPanel />
          <div className="reset-form-panel">
            <div className="reset-form-inner">
              <h2>Nueva contraseña</h2>
              <p className="reset-description">Elige una contraseña segura para tu cuenta.</p>

              {error && <div className="reset-error">{error}</div>}

              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="newPassword">Nueva contraseña</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nueva contraseña"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmar contraseña</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmar contraseña"
                    required
                  />
                </div>

                <div className="password-requirements">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className={`requirement ${req.valid ? 'valid' : ''}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {req.valid ? (
                          <polyline points="20 6 9 17 4 12" />
                        ) : (
                          <circle cx="12" cy="12" r="10" />
                        )}
                      </svg>
                      <span>{req.label}</span>
                    </div>
                  ))}
                </div>

                <button type="submit" className="reset-button" disabled={loading || !allRequirementsMet}>
                  {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
                </button>

                <Link to="/login" className="back-to-login">Volver al inicio de sesión</Link>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password sent
  if (forgotSent) {
    return (
      <div className="reset-page">
        <div className="reset-wrapper">
          <BrandPanel />
          <div className="reset-form-panel">
            <div className="reset-form-inner">
              <div className="success-message">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <h2>Revisa tu email</h2>
                <p>Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.</p>
                <Link to="/login" className="reset-button-link">Volver al inicio de sesión</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password form (no token)
  return (
    <div className="reset-page">
      <div className="reset-wrapper">
        <BrandPanel />
        <div className="reset-form-panel">
          <div className="reset-form-inner">
            <h2>Recuperar contraseña</h2>
            <p className="reset-description">Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.</p>

            {error && <div className="reset-error">{error}</div>}

            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="reset-button" disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </button>

              <Link to="/login" className="back-to-login">Volver al inicio de sesión</Link>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
